import { Deck } from "@/lib/classes/deck";
import type { DeckUnit } from "@/lib/types/deck-builder";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { Minus, ChevronUp } from "lucide-react";

interface CompactPackCardProps {
  deck: Deck;
  deckUnit: DeckUnit;
}

// Helper function to get veterancy icon
function getVeterancyIcon(veterancy: number) {
  if (veterancy === 0) {
    return <Minus className="h-2.5 w-2.5 stroke-[2.5]" />;
  } else if (veterancy === 1) {
    return <ChevronUp className="h-2.5 w-2.5 stroke-[2.5]" />;
  } else if (veterancy === 2) {
    return (
      <div className="flex flex-col items-center justify-center -space-y-0.5">
        <ChevronUp className="h-2 w-2 stroke-[2.5]" />
        <ChevronUp className="h-2 w-2 stroke-[2.5]" />
      </div>
    );
  } else if (veterancy === 3) {
    return (
      <div className="flex flex-col items-center justify-center -space-y-0.5">
        <ChevronUp className="h-1.5 w-1.5 stroke-[2.5]" />
        <ChevronUp className="h-1.5 w-1.5 stroke-[2.5]" />
        <ChevronUp className="h-1.5 w-1.5 stroke-[2.5]" />
      </div>
    );
  } else {
    return <span className="text-[8px] font-bold">{veterancy}</span>;
  }
}

export function CompactPackCard({ deck, deckUnit }: CompactPackCardProps) {
  const unit = deck.getUnitForPack(deckUnit.pack);
  if (!unit) return null;

  const veterancyQuantities = Deck.getVeterancyQuantitiesForPack(deckUnit.pack, unit);
  const quantity = veterancyQuantities[deckUnit.veterancy] || 0;

  const unitCost = unit.commandPoints || 0;
  const transportCost = deckUnit.transport?.commandPoints || 0;
  const totalCost = unitCost + transportCost;

  return (
    <div className="relative w-full h-full flex flex-col overflow-hidden rounded-md bg-muted/30 border border-border">
      {/* Unit Image */}
      <div className="relative w-full bg-muted/20" style={{ height: '60px' }}>
        <img
          src={getUnitImagePath(unit.descriptorName)}
          alt={unit.name}
          className="w-full h-full object-cover"
          style={{ display: 'block' }}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
          }}
        />

        {/* Command Points (top-right) */}
        <div className="absolute top-0 right-0 bg-black/80 backdrop-blur-sm px-1 py-0.5 rounded-bl-md text-white text-[10px] font-semibold z-10">
          {totalCost}
        </div>

        {/* Availability/Quantity (top-left) */}
        <div className="absolute top-0 left-0 bg-black/80 backdrop-blur-sm px-1 py-0.5 rounded-br-md text-white text-[10px] font-semibold z-10">
          x{quantity}
        </div>

        {/* Veterancy Indicator (top-right, below command points) */}
        <div className="absolute top-4 right-0 bg-black/80 backdrop-blur-sm px-1 py-0.5 rounded-bl-md text-white flex items-center justify-center z-10">
          {getVeterancyIcon(deckUnit.veterancy)}
        </div>

        {/* Transport Name (if present) - overlaying bottom of image */}
        {deckUnit.transport && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm px-1 py-0.5 text-white text-[9px] text-center truncate leading-tight z-10">
            {deckUnit.transport.name}
          </div>
        )}
      </div>

      {/* Unit Name (right below image, no gap) */}
      <div className="w-full bg-muted/90 px-1 py-0.5 text-center leading-none">
        <div className="text-[9px] font-medium text-foreground truncate">
          {unit.name}
        </div>
      </div>
    </div>
  );
}

